import React, { useState } from 'react';
import { CalendarClock, Plus, X, Play, Clock, Mail, Trash2, CheckCircle, Loader2 } from 'lucide-react';

interface ReportSchedule {
  id: string;
  name: string;
  type: 'daily' | 'weekly' | 'monthly';
  template: string;
  recipients: string[];
  enabled: boolean;
  lastSent: string | null;
  nextRun: string;
}

const TEMPLATES = [
  { id: 'overview', label: 'Gesamtübersicht', desc: 'Nutzer, Fragen, Abos, KI-Nutzung' },
  { id: 'users', label: 'Nutzer-Report', desc: 'Neue Nutzer, aktive Nutzer, Churn-Risiko' },
  { id: 'content', label: 'Content-Report', desc: 'Fragenqualität, Kapitelfortschritt' },
  { id: 'revenue', label: 'Umsatz-Report', desc: 'Premium-Conversions, Abo-Status' },
  { id: 'ai', label: 'KI-Report', desc: 'Token-Verbrauch, Kosten, Nutzung' },
];

export const ScheduledReportsPage: React.FC = () => {
  const [schedules, setSchedules] = useState<ReportSchedule[]>([
    { id: '1', name: 'Wöchentliche Gesamtübersicht', type: 'weekly', template: 'overview', recipients: ['qhconsulting.de@gmail.com'], enabled: true, lastSent: null, nextRun: getNextDate('weekly') },
    { id: '2', name: 'Monatlicher Umsatz-Report', type: 'monthly', template: 'revenue', recipients: ['qhconsulting.de@gmail.com'], enabled: false, lastSent: null, nextRun: getNextDate('monthly') },
  ]);
  const [showCreate, setShowCreate] = useState(false);
  const [newSchedule, setNewSchedule] = useState({ name: '', type: 'weekly' as const, template: 'overview', recipient: '' });
  const [sending, setSending] = useState<string | null>(null);

  function getNextDate(type: string): string {
    const d = new Date();
    if (type === 'daily') d.setDate(d.getDate() + 1);
    else if (type === 'weekly') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    return d.toISOString();
  }

  const toggleSchedule = (id: string) => {
    setSchedules(prev => prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s));
  };

  const deleteSchedule = (id: string) => {
    setSchedules(prev => prev.filter(s => s.id !== id));
  };

  const sendNow = async (id: string) => {
    setSending(id);
    await new Promise(r => setTimeout(r, 2000));
    setSchedules(prev => prev.map(s => s.id === id ? { ...s, lastSent: new Date().toISOString() } : s));
    setSending(null);
  };

  const createSchedule = () => {
    if (!newSchedule.name || !newSchedule.recipient) return;
    setSchedules(prev => [...prev, {
      id: Date.now().toString(), name: newSchedule.name, type: newSchedule.type,
      template: newSchedule.template, recipients: [newSchedule.recipient],
      enabled: true, lastSent: null, nextRun: getNextDate(newSchedule.type),
    }]);
    setShowCreate(false);
    setNewSchedule({ name: '', type: 'weekly', template: 'overview', recipient: '' });
  };

  const TYPE_LABELS = { daily: 'Taeglich', weekly: 'Woechentlich', monthly: 'Monatlich' };

  return (
    <div>
      <div className="page-header">
        <h1><CalendarClock size={24} /> Geplante Reports</h1>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> Report planen
        </button>
      </div>

      <div className="grid-cols-3 mb-6">
        <div className="kpi-card"><span className="kpi-label">Aktive Plaene</span><span className="kpi-value">{schedules.filter(s => s.enabled).length}</span></div>
        <div className="kpi-card"><span className="kpi-label">Gesamt</span><span className="kpi-value">{schedules.length}</span></div>
        <div className="kpi-card"><span className="kpi-label">Templates</span><span className="kpi-value">{TEMPLATES.length}</span></div>
      </div>

      {/* Templates Overview */}
      <div className="card mb-6">
        <h3 style={{ marginBottom: 12 }}>Verfügbare Templates</h3>
        <div className="grid-cols-3" style={{ gap: 8 }}>
          {TEMPLATES.map(t => (
            <div key={t.id} style={{ padding: 12, background: 'var(--bg-elevated)', borderRadius: 8, fontSize: 13 }}>
              <div style={{ fontWeight: 600 }}>{t.label}</div>
              <div className="text-xs text-muted">{t.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Schedules List */}
      <div className="table-container">
        <table>
          <thead><tr><th>Aktiv</th><th>Report</th><th>Intervall</th><th>Template</th><th>Empfaenger</th><th>Naechster Versand</th><th>Aktionen</th></tr></thead>
          <tbody>
            {schedules.map(s => (
              <tr key={s.id}>
                <td>
                  <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 20 }}>
                    <input type="checkbox" checked={s.enabled} onChange={() => toggleSchedule(s.id)} style={{ opacity: 0, width: 0, height: 0 }} />
                    <span style={{ position: 'absolute', cursor: 'pointer', inset: 0, background: s.enabled ? 'var(--primary)' : 'var(--border)', borderRadius: 20, transition: 'all 0.3s' }}>
                      <span style={{ position: 'absolute', height: 16, width: 16, left: s.enabled ? 17 : 2, top: 2, background: 'white', borderRadius: '50%', transition: 'all 0.3s' }} />
                    </span>
                  </label>
                </td>
                <td style={{ fontWeight: 600 }}>{s.name}</td>
                <td><span className="badge badge-info">{TYPE_LABELS[s.type]}</span></td>
                <td className="text-sm">{TEMPLATES.find(t => t.id === s.template)?.label}</td>
                <td className="text-xs">{s.recipients.join(', ')}</td>
                <td className="text-xs text-muted">{s.enabled ? new Date(s.nextRun).toLocaleDateString('de-DE') : '—'}</td>
                <td>
                  <div className="flex gap-1">
                    <button className="btn btn-secondary btn-sm" onClick={() => sendNow(s.id)} disabled={sending === s.id}>
                      {sending === s.id ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={12} />}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => deleteSchedule(s.id)}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 440, padding: 24 }}>
            <div className="flex items-center justify-between mb-4">
              <h3>Report planen</h3>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Name *</label>
                <input className="input" value={newSchedule.name} onChange={e => setNewSchedule(p => ({ ...p, name: e.target.value }))} placeholder="Report-Name" />
              </div>
              <div className="grid-cols-2" style={{ gap: 12 }}>
                <div>
                  <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Intervall</label>
                  <select className="input" value={newSchedule.type} onChange={e => setNewSchedule(p => ({ ...p, type: e.target.value as any }))}>
                    <option value="daily">Taeglich</option>
                    <option value="weekly">Woechentlich</option>
                    <option value="monthly">Monatlich</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Template</label>
                  <select className="input" value={newSchedule.template} onChange={e => setNewSchedule(p => ({ ...p, template: e.target.value }))}>
                    {TEMPLATES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Empfaenger E-Mail *</label>
                <input className="input" value={newSchedule.recipient} onChange={e => setNewSchedule(p => ({ ...p, recipient: e.target.value }))} placeholder="admin@fahryo.de" />
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Abbrechen</button>
                <button className="btn btn-primary" onClick={createSchedule} disabled={!newSchedule.name || !newSchedule.recipient}>
                  <Plus size={14} /> Erstellen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
